import type { FileMatch, HighlightedLines, SearchResult } from 'codesearch-backend'
import { createInfiniteQuery, createQuery } from '@tanstack/solid-query'
import { createEffect, createMemo, createSignal, on, untrack } from 'solid-js'
import { trpc } from '../trpc.ts'
import { navigate, route } from './router.ts'

const [draft, setDraft] = createSignal(route().kind === 'SEARCH' ? (route() as { query: string }).query : '')
const [debounced, setDebounced] = createSignal(draft())

/*
  a pasted /search?q=... or a back button press has to flow back into the box. `on`
  with an untracked draft is load-bearing: reading draft() as a dependency made this
  effect re-run on every keystroke, and clearing the box fast enough that the route
  hadn't caught up yet would see route=SEARCH "foo" against an empty draft and
  helpfully retype "foo". the box is the only writer of draft, this only follows it
*/
createEffect(on(route, (current) => {
  const next = current.kind === 'SEARCH' ? current.query : current.kind === 'HOME' ? '' : untrack(draft)
  if (next === untrack(draft)) return
  setDraft(next)
  setDebounced(next)
}))

/*
  the index you're currently in. searching from inside a repo searches that repo,
  the way code search scopes the box to whatever you're browsing - typing while
  looking at a file shouldn't silently search everything you own
*/
export const searchScope = createMemo(() => {
  const current = route()
  return current.kind === 'HOME' ? undefined : current.repo
})

let timer: ReturnType<typeof setTimeout> | undefined
export function setSearchQuery(value: string) {
  setDraft(value)
  clearTimeout(timer)

  // read now, not when the timer fires: what you were looking at as you typed is
  // what you meant to search
  const scope = untrack(searchScope)

  /*
    the url follows the debounced value rather than every keystroke. always with
    replace, or the back button would walk the query letter by letter - and browsers
    throttle replaceState (safari outright throws past its limit), so a per-keystroke
    url would eventually stop tracking the box and start disagreeing with it
  */
  timer = setTimeout(() => {
    setDebounced(value)

    // typing your way out of a file pushes once, so back returns to the file;
    // everything after that replaces
    const replace = route().kind === 'SEARCH'
    if (value.trim()) navigate({ kind: 'SEARCH', query: value, repo: scope }, { replace })
    else if (route().kind === 'SEARCH') navigate({ kind: 'HOME' }, { replace: true })
  }, 150)
}

/** drop the repo scope and search everything, keeping whatever is in the box */
export function clearSearchScope() {
  const query = untrack(draft)
  if (query.trim()) navigate({ kind: 'SEARCH', query })
  else navigate({ kind: 'HOME' })
}

export { draft as searchQuery }

/* the server highlights the matching lines on its way out, so the shapes the ui sees
   are the indexer's plus tokens */
export type SearchFileMatch = FileMatch & { tokens: HighlightedLines | null }
export type SearchData = Omit<SearchResult, 'files'> & { files: SearchFileMatch[] }

export const PAGE_SIZE = 50

/*
  an infinite query rather than a plain one: the indexer greps every candidate and
  orders the whole list before it truncates, so page 2 is genuinely the continuation
  of page 1 and not a second guess at the same question
*/
export function useSearchResults() {
  return createInfiniteQuery(() => ({
    queryKey: ['search', debounced(), searchScope()],
    queryFn: ({ pageParam }) => trpc.search.search.query({
      query: debounced(),
      repo: searchScope(),
      filesLimit: PAGE_SIZE,
      offset: pageParam,
    }),
    initialPageParam: 0,
    getNextPageParam: (last: SearchData, pages: SearchData[]) => {
      const loaded = pages.reduce((acc, it) => acc + it.files.length, 0)
      return loaded < last.fileTotal ? loaded : undefined
    },
    enabled: debounced().trim().length > 0,
    // the results stay put while the next query lands, so typing doesn't strobe
    placeholderData: (previous?: { pages: SearchData[], pageParams: number[] }) => previous,
  }))
}

export const isSearching = createMemo(() => draft() !== debounced())

export function useIndexerStatus() {
  return createQuery(() => ({
    queryKey: ['status'],
    queryFn: () => trpc.search.status.query(),
    refetchInterval: query => (query.state.data?.scanning ? 1000 : 5000),
  }))
}
